/**
 * distance-calculator.ts
 * A module for calculating distances between geographic coordinates
 */

import { LineString } from 'geojson';

export interface Coordinate {
  longitude: number;
  latitude: number;
}

/**
 * Calculates the distance between two geographic coordinates using the Haversine formula
 * @param point1 First coordinate point [longitude, latitude]
 * @param point2 Second coordinate point [longitude, latitude]
 * @returns Distance in miles
 */
export function calculateHaversineDistance(point1: [number, number], point2: [number, number]): number {
  const EARTH_RADIUS_MILES = 3958.8;
  
  const toRadians = (degrees: number): number => degrees * Math.PI / 180;

  const [lon1, lat1] = point1;
  const [lon2, lat2] = point2;

  const radLat1 = toRadians(lat1);
  const radLat2 = toRadians(lat2);
  const deltaLat = toRadians(lat2 - lat1);
  const deltaLon = toRadians(lon2 - lon1);

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
           Math.cos(radLat1) * Math.cos(radLat2) *
           Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_MILES * c;
}

/**
 * Calculates the total distance traveled through a series of coordinates
 * @param coords LineString object containing an array of [longitude, latitude] points
 * @returns Total distance in miles
 */
export function calculateTotalDistance(coords: LineString): number {
  
  if (coords.coordinates.length < 2) {
    return 0;
  }

  let totalDistance = 0;
  
  for (let i = 1; i < coords.coordinates.length; i++) {
    // Explicitly assert that coordinates are of type [number, number]
    const point1 = coords.coordinates[i - 1] as [number, number];
    const point2 = coords.coordinates[i] as [number, number];

    const distance = calculateHaversineDistance(point1, point2);
    totalDistance += distance;
  }
  console.log(`Total distance: ${totalDistance} miles`);
  return totalDistance;
}
